import { supabase } from './supabase';
import { callSiteApi } from '@/components/Sites/utils';
import type { Site } from '@/components/Sites/types';


// ─── Types ───────────────────────────────────────────────────────────────────

export interface FeedbackTicket {
  id: string;
  site_id: string;
  page_url: string;
  selector: string | null;
  x_percent: number;
  y_percent: number;
  scroll_y: number;
  viewport_width: number;
  viewport_height: number;
  screenshot_path: string | null;
  user_agent: string | null;
  comment: string;
  author_name: string;
  author_email: string | null;
  status: 'open' | 'in_progress' | 'review' | 'resolved';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
}

export interface FeedbackReply {
  id: string;
  ticket_id: string;
  author_name: string;
  author_email: string | null;
  author_role: 'client' | 'admin';
  content: string;
  created_at: string;
}

export interface FeedbackToken {
  id: string;
  site_id: string;
  token: string;
  is_active: boolean;
  allow_guests: boolean;
  widget_position: string;
  widget_theme?: string;
  created_at: string;
}

// ─── Ticket queries ───────────────────────────────────────────────────────────

export async function getFeedbackTickets(
  siteId: string,
  statusFilter?: string
): Promise<FeedbackTicket[]> {
  let query = supabase
    .from('feedback_tickets')
    .select('*')
    .eq('site_id', siteId)
    .order('created_at', { ascending: false });

  if (statusFilter && statusFilter !== 'all') {
    query = query.eq('status', statusFilter);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as FeedbackTicket[];
}

export async function updateTicketStatus(
  ticketId: string,
  status: FeedbackTicket['status'],
  priority?: FeedbackTicket['priority']
): Promise<void> {
  const updates: any = { status };
  if (priority) {
    updates.priority = priority;
  }
  const { error } = await supabase
    .from('feedback_tickets')
    .update(updates)
    .eq('id', ticketId);
  if (error) throw new Error(error.message);
}

export async function deleteFeedbackTicket(ticketId: string): Promise<void> {
  const { error } = await supabase
    .from('feedback_tickets')
    .delete()
    .eq('id', ticketId);
  if (error) throw new Error(error.message);
}

// ─── Replies ─────────────────────────────────────────────────────────────────

export async function getFeedbackReplies(ticketId: string): Promise<FeedbackReply[]> {
  const { data, error } = await supabase
    .from('feedback_replies')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as FeedbackReply[];
}

export async function createAdminReply(
  ticketId: string,
  content: string,
  authorName: string,
  authorEmail?: string,
  screenshotBase64?: string | null
): Promise<FeedbackReply> {
  let screenshotPath = null;
  
  if (screenshotBase64) {
    // Need the site_id for the folder path
    const { data: ticket } = await supabase.from('feedback_tickets').select('site_id').eq('id', ticketId).single();
    if (ticket?.site_id) {
      const { data, error } = await supabase.functions.invoke('feedback-api?action=admin_upload', {
        body: { site_id: ticket.site_id, screenshot_base64: screenshotBase64 }
      });
      if (error) {
        throw new Error(`Upload failed: ${error.message || JSON.stringify(error)}`);
      }
      if (data?.error) {
        throw new Error(`Upload error: ${data.error}`);
      }
      if (data?.path) {
        screenshotPath = data.path;
      }
    }
  }

  const { data, error } = await supabase
    .from('feedback_replies')
    .insert({
      ticket_id: ticketId,
      content,
      author_name: authorName,
      author_email: authorEmail ?? null,
      author_role: 'admin',
      screenshot_path: screenshotPath,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as FeedbackReply;
}

// ─── Tokens & settings ────────────────────────────────────────────────────────

export async function getFeedbackToken(siteId: string): Promise<FeedbackToken | null> {
  const { data, error } = await supabase
    .from('feedback_site_tokens')
    .select('*')
    .eq('site_id', siteId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  
  if (data?.widget_position) {
    const parts = data.widget_position.split(',');
    data.widget_position = parts[0];
    data.widget_theme = parts[1] || 'dark';
  } else if (data) {
    data.widget_theme = 'dark';
  }

  return data as FeedbackToken | null;
}

export async function activateFeedback(site: Site): Promise<FeedbackToken> {
  // 1. Call plugin to generate the token
  const pluginRes = await callSiteApi(
    site.url,
    site.publicKey,
    site.secretKey,
    'feedback/activate',
    'POST'
  );
  if (!pluginRes?.success || !pluginRes?.token) {
    throw new Error('Plugin did not return a feedback token');
  }
  const token: string = pluginRes.token;

  // 2. Upsert the token into Supabase
  const { data, error } = await supabase
    .from('feedback_site_tokens')
    .upsert(
      { site_id: site.id, token, is_active: true, widget_position: 'bottom-right,dark' },
      { onConflict: 'site_id' }
    )
    .select()
    .single();
  if (error) throw new Error(error.message);
  
  if (data?.widget_position) {
    const parts = data.widget_position.split(',');
    data.widget_position = parts[0];
    data.widget_theme = parts[1] || 'dark';
  } else {
    data.widget_theme = 'dark';
  }
  
  return data as FeedbackToken;
}

export async function deactivateFeedback(site: Site): Promise<void> {
  // 1. Tell the plugin to disable the widget
  await callSiteApi(site.url, site.publicKey, site.secretKey, 'feedback/deactivate', 'POST');

  // 2. Mark the token inactive in Supabase
  await supabase
    .from('feedback_site_tokens')
    .update({ is_active: false })
    .eq('site_id', site.id);
}

export async function updateFeedbackSettings(
  site: Site,
  allowGuests: boolean,
  widgetPosition: string,
  widgetTheme: string = 'dark',
  widgetAccentColor: string = ''
): Promise<void> {
  const combinedPosition = widgetAccentColor ? `${widgetPosition},${widgetTheme},${widgetAccentColor}` : `${widgetPosition},${widgetTheme}`;
  // 1. Push to plugin
  await callSiteApi(
    site.url,
    site.publicKey,
    site.secretKey,
    'feedback/settings',
    'POST',
    JSON.stringify({ allow_guests: allowGuests, widget_position: combinedPosition })
  );

  // 2. Update Supabase token row
  await supabase
    .from('feedback_site_tokens')
    .update({ allow_guests: allowGuests, widget_position: combinedPosition })
    .eq('site_id', site.id);
}

// ─── Screenshot URL ──────────────────────────────────────────────────────────

export function getScreenshotUrl(path: string | null): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  
  // Use the Cloudflare R2 custom domain
  const baseUrl = 'https://feedbacks.agencyplus.io';
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${baseUrl}/${cleanPath}`;
}
